/**
 * @name useAccLogic.js
 * @author 김권영
 * @since 2025. 02. 26.
 * @description
 * 사업장(관리자 조회), 공정별 설비의 재해 발생 형태 매핑 관리 로직을 담당하는 React 훅
 * - 공정별 설비 목록 조회
 * - 재해 발생 형태 목록 조회
 * - 선택 후 매핑
 * 
 * @param {string} compCode - 관리자 : 선택된 사업장 코드(전역), 유저 : 로그인 정보에 담긴 사업장 코드
 * @returns {Object} - 함수와 상태를 반환
 */
import { useState, useEffect, useCallback  } from "react";
import { get, post } from "@utils/axios_api";
import { toast } from 'react-toastify';
export function useAccLogic(compCode) {
  // 회사 목록과 재해 구분 목록
  const [company, setCompany] = useState([]);
  const [seriousAccidentByCategory, setSeriousAccidentByCategory] = useState([]);

  // 가운데(재해발생형태)와 오른쪽(재해발생형태목록), 선택된 공정
  const [centerAccidentList, setCenterAccidentList] = useState([]);
  const [rightAccidentList, setRightAccidentList] = useState([]);
  const [selectedProcEqpt, setSelectedProcEqpt] = useState(null);

  // [초기 데이터] 회사목록, 구분목록 (공통)
  const fetchInitialData = useCallback(async () => {
    try {
      const data = await get("/user/acc", {
        params: { compCode:compCode },
      });
      setCompany(data.company || []);
      setSeriousAccidentByCategory(data.seriousAccidentByCategory || []);
    } catch (err) {
      console.error(err);
    }
  }, [compCode]);

  // compCode 변경 시, 가운데/오른쪽/선택 공정 초기화
  useEffect(() => {
    setSelectedProcEqpt(null);
    setCenterAccidentList([]);
    setRightAccidentList([]);
  }, [compCode]);

  /**
   * 모든 재해 매핑 데이터를 저장하는 함수.
   *
   * @param {string} compCodeParam - 회사 코드.
   * @param {int} mpreqCodeParam - 선택된 공정별 설비 코드.
   * @param {string} categoryCodeParam - (옵션) 선택된 재해 발생 형태의 카테고리 코드.
   */
  async function handleSaveAll(compCodeParam, mpreqCodeParam, categoryCodeParam) {
    if (!mpreqCodeParam) {
      toast.warn("공정(설비)를 먼저 선택하세요.");
      return;
    }
    if (!window.confirm("저장 하시겠습니까?")) return;
    try {
      const payload = centerAccidentList.map((r) => ({
        maccCode: r.maccCode ?? 0,
        mpreqCode: mpreqCodeParam,
        saccCode: r.saccCode,
        saccYn: r.saccYn === 1 ? 1 : 0,
      }));
      const response = await post("/user/accSave", {
        payload,
        mpreqCode: mpreqCodeParam,
      });
      toast.success(response.message);
      // 저장 후 가운데 데이터 재조회
      await fetchCenterAccidentList(compCodeParam, mpreqCodeParam);
      if (categoryCodeParam) {
        await fetchRightAccidentList({ compCodeParam, mpreqCodeParam, categoryCodeParam });
      }
    } catch (err) {
      console.error("저장 오류:", err);
    }
  }

  /**
   * 가운데 테이블(재해발생형태) 데이터를 조회하는 함수.
   *
   * @param {string} compCodeParam - 회사 코드.
   * @param {int} mpreqCodeParam - 선택된 공정별 설비 코드.
   */
  async function fetchCenterAccidentList(compCodeParam, mpreqCodeParam) {
    try {
      const data = await get("/user/accMapping", {
        params: { compCode: compCodeParam,
                  mpreqCode: mpreqCodeParam },
      });
      setCenterAccidentList(data.accidentMappingList || []);
    } catch (err) {
      console.error("가운데 테이블 조회 오류:", err);
    }
  }
    
  /**
   * 오른쪽 테이블(재해 발생 형태 목록) 데이터를 조회하는 함수.
   *
   * @param {string} compCodeParam - 회사 코드.
   * @param {int} mpreqCodeParam - 선택된 공정별 설비 코드.
   * @param {string} categoryCodeParam - 재해 발생 형태의 카테고리 코드.
   */
  async function fetchRightAccidentList({ compCodeParam, mpreqCodeParam, categoryCodeParam }) {
    try {
      const data = await get("/user/accident", {
        params: { 
            compCode: compCodeParam, 
            mpreqCode: mpreqCodeParam || 0, 
            categoryCode: categoryCodeParam || "" },
      });
      setRightAccidentList(data.accidentList || []);
    } catch (err) {
      console.error("오른쪽 테이블 조회 오류:", err);
    }
  }
  
  /**
   * 가운데 테이블의 행 클릭 이벤트 핸들러.
   * @param {string} saccCode - 클릭한 재해 발생 형태의 코드.
   */
  function handleCenterRowClick(saccCode) {
    setCenterAccidentList((prev) =>
      prev.map((r) =>
        r.saccCode === saccCode ? { ...r, isChecked: !r.isChecked } : r
      )
    );
  }

  /**
   * 오른쪽 테이블의 행 클릭 이벤트 핸들러.
   * @param {string} saccCode - 클릭한 재해 발생 형태의 코드.
   */
  function handleRightRowClick(saccCode) {
    setRightAccidentList((prev) =>
      prev.map((r) =>
        r.saccCode === saccCode ? { ...r, isChecked: !r.isChecked } : r
      )
    );
  }

  // 오른쪽 테이블에서 선택된 항목들을 가운데 테이블로 이동 시키는 함수 
  function moveRightToCenter() {
    const selected = rightAccidentList.filter((r) => r.isChecked);
    if (selected.length === 0) return;
    const itemsToCenter = selected.map((it) => ({ ...it, isChecked: false }));
    setCenterAccidentList((prev) => [...prev, ...itemsToCenter]);
    setRightAccidentList((prev) => prev.filter((r) => !r.isChecked));
  }

  // 가운테 테이블에서 선택된 항목들을 오른쪽 테이블로 이동 시키는 함수
  function moveCenterToRight() {
    const selected = centerAccidentList.filter((r) => r.isChecked);
    if (selected.length === 0) return;
    const itemsToRight = selected.map((it) => ({ ...it, isChecked: false }));
    setRightAccidentList((prev) => [...prev, ...itemsToRight]);
    setCenterAccidentList((prev) => prev.filter((r) => !r.isChecked));
  }

  return {
    company, centerAccidentList, rightAccidentList, seriousAccidentByCategory,
    selectedProcEqpt,
    setSelectedProcEqpt, setCenterAccidentList, setRightAccidentList,
    fetchInitialData, fetchCenterAccidentList, fetchRightAccidentList,
    handleSaveAll, handleCenterRowClick, handleRightRowClick,
    moveRightToCenter, moveCenterToRight,
  };
}
